import {Timestamp} from '@firebase/firestore';
import {isFutureTimestamp} from './date-utils';
import {planNames, teamPlanNames} from '../common/text';
import dayjs from "dayjs";
import {SupportedLanguage} from './supported-language.model';
import {AffiliateSettings} from './affiliate-settings.model';
import {AffiliateProgramCookie} from './affiliate-program-cookie.model';
import { CommunityMemberSettings } from './community/community-member-settings.model';


export interface User {
  id: string;
  email?: string;
  invitationStatus?: "active" | "invited";
  unreadNotificationsCounter?:number;
  pictureUrl?: string;
  displayName?: string;
  pricingPlan?: string;
  isTeamPlan?: boolean;
  maxTeamSize?: number;
  planActivatedAt?: Timestamp;
  planEndsAt?: Timestamp;
  userCreatedAt?: Timestamp;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  cardLast4Digits?: string;
  cardExpirationMonth?: string;
  cardExpirationYear?: string;
  testUser?: boolean;
  addedToNewsletter?: boolean;
  purchasedCourses?: string[];
  temporaryAccess?: any;
  leadReportedToFacebook?: boolean;
  unsubscribeFromNewsletter?: boolean;
  newsletterBlockedReason?: 'bounce' | 'complaint';
  preferredLanguage?: SupportedLanguage;
  groupIds?: string[];
  groupNames?: string[];
  affiliateStatus?: 'active' | 'inactive';
  affiliateId?:string; // this is the unique affiliate ID of the user, in case they joined the creator affiliate program
  affiliateProgramCookie?: AffiliateProgramCookie;
  affiliateAmountDue?: number;
  affiliateAmountPaid?: number;
  affiliateSettings?: AffiliateSettings;
  isCommunityMember?: boolean;
  isActiveCommunityMember?: boolean;
  communityMemberSettings?: CommunityMemberSettings;
  lastSeenAt?: Timestamp;
  mixPanelInitialized?: boolean;
  invitationLinkId?: string;
  studentEnrollment?: StudentEnrollment;
  firstPaymentMade?: boolean;
  maxAssessmentAttempts?: MaxAssessmentAttempts;
  progressMatrix?: ProgressMatrix;
}

export interface ProgressMatrix {
  [courseId: string]: number;
}
export interface MaxAssessmentAttempts {
  [assessmentId: string]: number;
}

export interface StudentEnrollment {
  [x: string]: string;
}

export const ANONYMOUS_USER: User = {
  id: 'anonymous',
  testUser: false
};

export function isAnonymousUser(user: User) {
  return user && user.id == 'anonymous';
}

export function isUserPlanStillValid(user: User) {
  return user.pricingPlan && (!user.planEndsAt || isFutureTimestamp(user.planEndsAt));
}

export function isUserPlanCanceled(user: User) {
  return user.pricingPlan && user.planEndsAt;
}

export function subscriptionDescription(user: User) {
  if (!user.pricingPlan) {
    return 'None';
  }
  if (!user.isTeamPlan) {
    return planNames[user.pricingPlan];
  }
  if (user.isTeamPlan) {
    return teamPlanNames[user.pricingPlan];
  }
}

export function isSubscriptionActive(user: User) {
  return user && user.pricingPlan && (!user.planEndsAt || dayjs(user.planEndsAt.toMillis()).isAfter(dayjs()));
}

export function isSubscriptionCancelled(user: User) {
  return user && user.planEndsAt;
}

export function isLifetimeSubscriber(user: User) {
  return user.pricingPlan == "lifetime";
}

export function planActivationDate(user) {
  return new Date(user.planActivatedAt.toMillis());
}

export function planValidUntil(user: User) {
  return new Date(user?.planEndsAt?.toMillis() ?? new Date());
}

export function planEndsAtInDaysFromNow(user: User) {
  if (user && user.planEndsAt) {
    const now = dayjs();
    const planEndsAt = dayjs(user.planEndsAt.toMillis());
    const diff = planEndsAt.diff(now, 'day');
    return diff <= 7 ? diff : 0;
  } else {
    return 0;
  }
}
